import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ORIGIN, publicPage, SITE } from '../seo';
import { useSession } from '../lib/session';
import { HubProvider, useHub, usePrefetch } from './context';
import { loadGear, loadStash } from './character';
import { Art, Draw, Ghost, Message, Pic, SmallButton } from './ui';
import OperationsTab from './tabs/OperationsTab';
import CharacterTab from './tabs/CharacterTab';
import TriumphsTab from './tabs/TriumphsTab';
import CollectionsTab from './tabs/CollectionsTab';
import QuestsTab from './tabs/QuestsTab';
import ActivityTab from './tabs/ActivityTab';
import LegendTab from './tabs/LegendTab';
import ClanTab from './tabs/ClanTab';
import MapsTab from './tabs/MapsTab';
import D2InfoTab from './tabs/D2InfoTab';
import TrackerTab from './tabs/TrackerTab';
import ItemManager, { ITEMS_NOTE } from './tabs/ItemManager';
import { PrivateGate } from './tabs/CharacterTab';
import './guardian.css';
import './clan.css';
import './activity.css';
import './triumphs.css';
import './collections.css';
import './quests.css';
import './maps.css';
import ErrorBoundary from '../lib/ErrorBoundary';
import Landing from './Landing';

/**
 * The Guardian hub (port of the app's GuardianHubPage): the emblem's banner across the top with the name, ranks and
 * Power, a text tab strip with a neon underline, the page's own sub-pages as icon tiles down the left.
 */

export const TABS = ['Clan', 'Collections', 'Triumphs', 'Maps', 'Operations', 'Character', 'Quests', 'Activity', 'Legend'] as const;
export type Tab = (typeof TABS)[number];

// each tab's sub-pages: key, tooltip, drawing, the short name under it
export const SUB_PAGES: Partial<Record<Tab, { key: string; tip: string; art: string; label: string }[]>> = {
  Operations: [
    { key: 'Operations', tip: 'Operations: what is on right now', art: Draw.Target, label: 'Operations' },
    { key: 'Seasonal Hub', tip: 'Seasonal Hub: your season rank, orders and challenges', art: Draw.SeasonCalendar, label: 'Season' },
    { key: 'Rotations', tip: "Rotations: this week's raid rotation", art: Draw.Cycle, label: 'Rotations' },
    { key: 'D2 Info', tip: 'D2 Info: look up any weapon or armour, with god rolls', art: Draw.Book, label: 'D2 Info' },
    { key: 'Xur', tip: 'Xûr: whether he is here and what he sells this week', art: Draw.Xur, label: 'Xûr' },
    { key: 'Item Manager', tip: 'Item Manager: your characters and vault side by side; drag items to move or equip them', art: Draw.VaultDoor, label: 'Items' },
  ],
  Triumphs: [
    { key: 'Active', tip: 'Active Triumphs and Titles', art: Draw.Trophy, label: 'Active' },
    { key: 'Legacy', tip: 'Legacy Triumphs and Titles', art: Draw.Hourglass, label: 'Legacy' },
    { key: 'Explorer', tip: 'Explorer: the whole tree, opened node by node', art: Draw.Tree, label: 'Explorer' },
    { key: 'Journey', tip: 'Journey: the Guardian Ranks', art: Draw.Ranks, label: 'Journey' },
  ],
  Collections: [
    { key: 'Items', tip: 'Collections: every item you have found', art: Draw.ItemGrid, label: 'Items' },
    { key: 'Explorer', tip: 'Explorer: the whole tree, opened node by node', art: Draw.Tree, label: 'Explorer' },
    { key: 'Sets', tip: 'Sets', art: Draw.Layers, label: 'Sets' },
    { key: 'Tenets', tip: 'Tenets: the Tenet shops and what they offer', art: Draw.Pillar, label: 'Tenets' },
    { key: 'Armor Set Bonuses', tip: 'Armor Set Bonuses', art: Draw.ShieldPlus, label: 'Set Bonuses' },
    { key: 'Lore', tip: 'Lore', art: Draw.Book, label: 'Lore' },
    { key: 'Patterns & Catalysts', tip: 'Patterns & Catalysts', art: Draw.Pattern, label: 'Patterns' },
    { key: 'Medals', tip: 'Medals', art: Draw.Medal, label: 'Medals' },
    { key: 'Stat Trackers', tip: 'Stat Trackers', art: Draw.Bars, label: 'Trackers' },
    { key: 'Item Manager', tip: 'Item Manager: your characters and vault side by side; drag items to move or equip them', art: Draw.VaultDoor, label: 'Item Manager' },
  ],
  Clan: [
    { key: 'About', tip: 'About the clan', art: Draw.Banner, label: 'About' },
    { key: 'Roster', tip: 'Roster: every member', art: Draw.People, label: 'Roster' },
    { key: 'Admin', tip: 'Admin', art: Draw.Crown, label: 'Admin' },
    { key: 'Relationships', tip: 'Relationships: who you play with', art: Draw.Heart, label: 'Friends' },
    { key: 'Historical Stats', tip: 'Historical Stats', art: Draw.Graph, label: 'Stats' },
    { key: 'Exotics', tip: 'Exotics: who owns what', art: Draw.Engram, label: 'Exotics' },
  ],
  Character: [
    { key: 'Items', tip: 'Items: what each character wears and carries, and the vault; drag items to move or equip them', art: Draw.ItemGrid, label: 'Items' },
    { key: '3D View', tip: '3D view: your Guardian in 3D with shaders, ornaments and subclass', art: Draw.Cube, label: '3D View' },
    { key: 'Inventory', tip: 'Inventory: Postmaster, consumables, materials and mods', art: Draw.Backpack, label: 'Inventory' },
    { key: 'Vendors', tip: 'Vendors: what the vendors sell and your rank with each', art: Draw.Stall, label: 'Vendors' },
  ],
  Quests: [
    { key: 'Bounties', tip: 'Bounties', art: Draw.Checklist, label: 'Bounties' },
    { key: 'All Quests', tip: 'All Quests', art: Draw.QuestMarker, label: 'Quests' },
    { key: 'Exotics', tip: 'Exotics: exotic gear and catalyst quests', art: Draw.Engram, label: 'Exotics' },
    { key: 'Pathfinder', tip: 'Pathfinder', art: Draw.Compass, label: 'Pathfinder' },
  ],
  Activity: [
    { key: 'Carnage', tip: 'Carnage: your post game carnage reports', art: Draw.Swords, label: 'Carnage' },
    { key: 'Heat', tip: 'Heat: when you played', art: Draw.Flame, label: 'Heat' },
    { key: 'Hours', tip: "Hours: where your time went, and beside a friend's", art: Draw.Hourglass, label: 'Hours' },
    { key: 'Bookmarks', tip: 'Bookmarks: the activities you starred', art: Draw.Ribbon, label: 'Bookmarks' },
    { key: 'Series', tip: 'Series: clears and best times', art: Draw.Stopwatch, label: 'Series' },
    { key: 'D2 Tracker', tip: 'D2 Tracker: recent matches, match reports and weekly private-match points', art: Draw.Graph, label: 'Tracker' },
  ],
};

export const slug = (s: string) => s.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** The hub's address: /guardian/<tab>/<sub>. */
export function useHubRoute() {
  const params = useParams();
  const nav = useNavigate();
  const tab = (TABS.find((t) => slug(t) === params.tab) ?? 'Operations') as Tab;
  const pages = SUB_PAGES[tab] ?? [];
  const sub = pages.find((p) => slug(p.key) === params.sub)?.key ?? pages[0]?.key ?? '';
  const go = (t: Tab, s?: string) => {
    const first = SUB_PAGES[t]?.[0]?.key;
    const path = `/guardian/${slug(t)}${s && s !== first ? `/${slug(s)}` : ''}`;
    nav(path);
  };
  return { tab, sub, go };
}

export default function GuardianHub() {
  return (
    <HubProvider>
      <HubFrame />
    </HubProvider>
  );
}

function HubFrame() {
  const h = useHub();
  const session = useSession();
  const { tab, sub, go } = useHubRoute();
  useEffect(() => { window.scrollTo({ top: 0 }); }, [tab, sub]);
  useUiScale();   // before any early return: hooks run in the same order every time
  // the 3D View's gear and the Item Manager's stash start with the hub's own read, not after it (about 2 s sooner)
  usePrefetch(tab === 'Character' && sub === '3D View' ? 'gear' : undefined, (t) => loadGear(h.player!, t || undefined), { public: true });
  usePrefetch((tab === 'Character' && sub === 'Items') || sub === 'Item Manager' ? 'stash' : undefined, (t) => loadStash(h.player!, t));

  usePageHead();

  // signed in, while the hub finds who that is: the wait (signed out, or still asking, the site is shown straight away)
  if (!h.player && session.status === 'signedIn' && !h.error) return <div className="g-root g-signpage"><div className="g-lights" /><div className="g-bigwait"><Ghost size={84} /><p className="g-sweeptext">Finding your Guardian...</p></div></div>;

  return (
    <div className={`g-root ${tab === 'Maps' || (tab === 'Character' && sub === '3D View') ? 'maps' : ''} ${(tab === 'Character' && sub === 'Items') || sub === 'Item Manager' ? 'items' : ''} ${tab === 'Character' && sub === '3D View' ? 'view3d' : ''}`}>
      <div className="g-lights" />
      <Header />
      <div className="g-scroll">
        <div className="g-page">
          <SideNav tab={tab} sub={sub} go={go} />
          {/* a page starts afresh for another character, except the Item Manager, which switches its own (keeping its
              search, filters and place) */}
          <div className="g-body" key={`${tab}/${sub}/${(tab === 'Character' && sub === 'Items') || sub === 'Item Manager' ? '' : h.char?.id ?? ''}`}>
            <ErrorBoundary><Page tab={tab} sub={sub} /></ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The hub grows with the window as the app's pages do: drawn for a window up to 2133 px wide, everything (text, tiles,
 * panels, gaps) is enlarged in proportion beyond that (a 2560 px window: 1.2x, the owner's chosen size, which is how
 * the site looked on that screen at 75% browser zoom with the old 1600 px start; the most, 1.6x), instead of a wide
 * screen only adding empty space round small content. --ui on the page root; guardian.css applies it as CSS zoom to the
 * header and the page (not the Maps and 3D View stages, which fill the window themselves and whose dragging maths is in
 * screen pixels). Phones and narrow windows stay at 1.
 *
 * The width is the window's own, outerWidth, which the browser's zoom leaves alone: innerWidth grows when the page is
 * zoomed out, so the scale grew with it and cancelled the zoom (75% and 100% looked the same). This way the site picks
 * one size for the screen and the browser's zoom still makes it smaller or bigger, as on any site.
 */
function useUiScale() {
  useEffect(() => {
    const set = () => document.documentElement.style.setProperty('--ui', String(Math.min(1.6, Math.max(1, (window.outerWidth || window.innerWidth) / 2133)).toFixed(3)));
    set();
    window.addEventListener('resize', set);
    return () => { window.removeEventListener('resize', set); document.documentElement.style.removeProperty('--ui'); };
  }, []);
}

/**
 * The pages that need nobody signed in or looked up: they read only Bungie's public lists (with the site's key) and
 * the site's own files, so they are shown to everyone straight away.
 */
const OPEN_PAGES = new Set(['Operations/Operations', 'Operations/Rotations', 'Operations/Xur', 'Operations/D2 Info', 'Collections/D2 Info', 'Maps/']);
const isOpen = (tab: Tab, sub: string) => OPEN_PAGES.has(`${tab}/${sub}`) || sub === 'D2 Info';

/** The <title>, description and canonical address of the page shown (the worker sends them with the first page; this keeps them right as people move around). */
function usePageHead() {
  const loc = useLocation();
  useEffect(() => {
    const info = publicPage(loc.pathname, loc.search);
    const { tab, sub } = routeOf(loc.pathname);
    document.title = info?.title ?? `${sub && sub !== tab ? `${sub === 'Xur' ? 'Xûr' : sub} · ` : ''}${tab} | ${SITE}`;
    const set = (sel: string, make: () => HTMLElement, attr: string, value: string) => {
      let el = document.head.querySelector<HTMLElement>(sel);
      if (!el) { el = make(); document.head.appendChild(el); }
      el.setAttribute(attr, value);
    };
    if (info) set('meta[name="description"]', () => Object.assign(document.createElement('meta'), { name: 'description' }), 'content', info.description);
    set('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' }), 'href', `${ORIGIN}${info?.path ?? loc.pathname}${info && loc.pathname.endsWith('/d2-info') && new URLSearchParams(loc.search).get('item') ? `?item=${encodeURIComponent(new URLSearchParams(loc.search).get('item')!)}` : ''}`);
  }, [loc.pathname, loc.search]);
}
const routeOf = (path: string) => {
  const [, , t, s] = path.split('/');
  const tab = (TABS.find((x) => slug(x) === t) ?? 'Operations') as Tab;
  const pages = SUB_PAGES[tab] ?? [];
  return { tab, sub: pages.find((p) => slug(p.key) === s)?.key ?? pages[0]?.key ?? '' };
};

function Page({ tab, sub }: { tab: Tab; sub: string }) {
  const h = useHub();
  const session = useSession();
  const open = isOpen(tab, sub);
  if (!h.player && !open) return session.status === 'loading' ? <Message title="One moment" text="Checking whether you are signed in..." /> : <Landing need={sub || tab} />;
  if (h.player && !open) {
    if (h.error && !h.hub) return <Message title="Your Guardian could not be loaded" text={`${h.error}\n\nClick Refresh (top right) to try again.`} />;
    if (!h.hub) return <Message title="Loading your Guardian" text="Asking Bungie for your characters and triumphs..." />;
  }
  if (sub === 'D2 Info') return <><h1 className="g-sr">D2 Info: weapon and armour look-up</h1><D2InfoTab /></>;
  if (sub === 'D2 Tracker') return <TrackerTab />;
  if (sub === 'Item Manager') return <PrivateGate title="Item Manager" what={ITEMS_NOTE}><ItemManager /></PrivateGate>;   // a side tile on Operations and Collections
  switch (tab) {
    case 'Operations': return <OperationsTab sub={sub} />;
    case 'Character': return <CharacterTab sub={sub} />;
    case 'Triumphs': return <TriumphsTab sub={sub} />;
    case 'Collections': return <CollectionsTab sub={sub} />;
    case 'Quests': return <QuestsTab sub={sub} />;
    case 'Activity': return <ActivityTab sub={sub} />;
    case 'Legend': return <LegendTab />;
    case 'Clan': return <ClanTab sub={sub} />;
    case 'Maps': return <><h1 className="g-sr">Destiny 2 maps</h1><MapsTab /></>;
    default: return <NotYet tab={tab} sub={sub} />;
  }
}

/** The tabs still being brought over from the app. */
function NotYet({ tab, sub }: { tab: string; sub: string }) {
  return (
    <div className="g-message">
      <h2>{sub || tab}</h2>
      <p className="g-text2">This part is being brought over from the app next.</p>
    </div>
  );
}

// ---------------- the header ----------------

function Header() {
  const h = useHub();
  const session = useSession();
  const { tab, go } = useHubRoute();
  const c = h.char;
  const [menu, setMenu] = useState(false);
  const seasonRank = c && h.season ? h.hub?.progress(c.id, h.season.rewardProgression) : undefined;
  const title = c?.titleRecord && h.defs ? (c.female ? h.defs.records.get(c.titleRecord)?.titleFemale : h.defs.records.get(c.titleRecord)?.title) : '';

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menu]);

  return (
    <header className="g-head">
      {c?.banner && <Pic src={c.banner} className="g-banner" key={c.banner} />}
      <div className="g-head-shade" />
      <div className="g-head-row">
        <div className="g-emblem-wrap">
          {c && (
            <button type="button" className={`g-emblem ${menu ? 'open' : ''}`} title="Switch character" aria-haspopup="menu" aria-expanded={menu} onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }}>
              <Pic src={c.emblem} />
              {(h.hub?.chars.length ?? 0) > 1 && <span className="g-emblem-swap" aria-hidden="true"><ChevronIcon /></span>}
            </button>
          )}
          {menu && h.hub && (
            <div className="g-menu" role="menu">
              {h.hub.chars.map((ch) => (
                <button key={ch.id} type="button" role="menuitem" onClick={() => { h.setChar(ch.id); setMenu(false); }}>
                  <Pic src={ch.emblem} width={28} height={28} />
                  <span><b>{ch.cls}</b> <span className="gold">{ch.light}</span></span>
                  <small>{ch.race}, last played {ch.lastPlayed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</small>
                  {ch.id === c?.id && <span className="tick">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="g-who">
          <div className="g-nameline">
            <div className="g-name">{h.hub ? h.hub.name.split('#')[0] : h.player?.name.split('#')[0] ?? 'D2 Companion'}</div>
            {title && <span className="g-title-badge" title="Equipped title">{title}</span>}
          </div>
          {h.hub && c && (
            <div className="g-facts">
              <Fact icon={<BoltIcon />} value={c.light} tip="Power" gold />
              {h.hub.guardianRank > 0 && <Fact icon={<StarIcon />} value={h.hub.guardianRank} tip="Guardian rank" />}
              {seasonRank && seasonRank.level > 0 && <Fact icon={<FlagIcon />} value={seasonRank.level} tip="Season rank" />}
              <span className="g-facts-sub">{[c.cls, c.race].filter(Boolean).join(' · ')}</span>
            </div>
          )}
          {seasonRank && seasonRank.size > 0 && (
            <div className="g-rank" title={`Season rank ${seasonRank.level}: ${seasonRank.into.toLocaleString()} / ${seasonRank.size.toLocaleString()} to the next rank`}>
              <div className="g-rankbar">
                <div style={{ width: `${Math.min(1, seasonRank.into / seasonRank.size) * 100}%` }} />
              </div>
              <span className="g-rank-text">{Math.floor(Math.min(1, seasonRank.into / seasonRank.size) * 100)}% to {seasonRank.level + 1}</span>
            </div>
          )}
        </div>
        <div className="g-head-right">
          <div className="g-head-top">
            {h.status && <span className={`g-status ${h.loading ? 'busy' : ''}`}><i aria-hidden="true" />{h.status}</span>}
            {session.status === 'signedIn' && h.own && <SmallButton title="Sign out of Bungie on this site" onClick={() => void session.signOut()}>Sign out</SmallButton>}
            {session.status === 'signedIn' && !h.own && h.player && <SmallButton onClick={h.clearLookup} title="Back to your own Guardian">Show mine</SmallButton>}
            {session.status === 'signedOut' && h.player && <SmallButton onClick={h.clearLookup} title="Look up someone else">Change Guardian</SmallButton>}
            {session.status === 'signedOut' && <SmallButton onClick={session.signIn}>Sign in</SmallButton>}
            <button type="button" className={`g-small g-refresh ${h.loading ? 'spin' : ''}`} onClick={h.refresh} disabled={h.loading || !h.player} title="Fetch the latest from Bungie">
              <RefreshIcon />Refresh
            </button>
          </div>
          <Tabs tab={tab} go={go} />
        </div>
      </div>
    </header>
  );
}

function Fact({ icon, value, tip, gold }: { icon: ReactNode; value: number; tip: string; gold?: boolean }) {
  return <span className={`g-fact ${gold ? 'gold' : ''}`} title={tip}>{icon}<b>{value}</b></span>;
}
const FlagIcon = () => <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M3.5 14.5V2M3.5 2.5h8.5l-2 3 2 3H3.5" /></svg>;
const StarIcon = () => <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"><path d="M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.2L8 11.5l-3.8 2 .7-4.2-3.1-3 4.3-.6z" /></svg>;
const ChevronIcon = () => <svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6l4 4 4-4" /></svg>;
const RefreshIcon = () => <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v3h-3" /></svg>;
const BoltIcon = () =><svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"><path d="M9.5 1.5L3.5 9h4l-1 5.5 6-7.5h-4z" /></svg>;

/** The text tabs, with a neon line that glides under the chosen one. */
function Tabs({ tab, go }: { tab: Tab; go: (t: Tab) => void }) {
  const row = useRef<HTMLDivElement>(null);
  const [line, setLine] = useState({ x: 0, w: 0 });
  useLayoutEffect(() => {
    const place = () => {
      const el = row.current?.querySelector<HTMLElement>('.on');
      if (el) setLine({ x: el.offsetLeft, w: el.offsetWidth });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [tab]);
  return (
    <nav className="g-tabs" ref={row} aria-label="Guardian hub">
      {TABS.map((t) => (
        <button key={t} type="button" className={t === tab ? 'on' : ''} onClick={() => go(t)}>{t}</button>
      ))}
      <span className="g-tabline" style={{ transform: `translateX(${line.x}px)`, width: line.w }} />
    </nav>
  );
}

/** The sub-page buttons down the left: the app's own drawings, the chosen one lit. */
function SideNav({ tab, sub, go }: { tab: Tab; sub: string; go: (t: Tab, s?: string) => void }) {
  const pages = SUB_PAGES[tab];
  if (!pages) return <div className="g-side" />;
  return (
    <nav className="g-side" aria-label={`${tab} pages`}>
      {pages.map((p) => (
        <button key={p.key} type="button" className={`g-sidetile ${p.key === sub ? 'on' : ''}`} title={p.tip} onClick={() => go(tab, p.key)}>
          <Art d={p.art} size={28} />
          <span>{p.label}</span>
        </button>
      ))}
    </nav>
  );
}
